import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../lib/i18n";
import api from "../lib/api";
import { ROLE_LABELS } from "../lib/ui";
import { useConfig } from "../lib/useConfig";
import { Logo } from "./Logo";
import { OfflineBar } from "./OfflineBar";
import { InstallPrompt } from "./InstallPrompt";
import {
  LayoutDashboard, FilePlus2, ClipboardList, Gauge, CalendarRange, Presentation,
  ShieldCheck, Siren, ListTodo, MapPinned, Building2, Settings, LogOut, Menu, X, Map, UploadCloud, FlaskConical, Languages, Layers, Layers3, UserCircle, LayoutGrid, MailCheck, FileText, ClipboardPen, FileStack, ClipboardCheck, DownloadCloud, Wrench, ChevronDown, Library,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const OV = ["provincial_supervisor", "si_analyst", "program_manager", "admin"];
const SURV = ["field_epi", ...OV];
const ADV = ["si_analyst", "admin"];
const PILLAR = ["lab_tech", "rcce_agent", "eds_agent", "ipc_agent", "case_mgmt_agent"];

const NAV = [
  { to: "/overview", label: "Vue d'ensemble", icon: LayoutDashboard, roles: SURV },
  { to: "/entry", label: "Ma journée", icon: ClipboardPen, roles: ["field_epi", ...PILLAR] },
  { to: "/generated", label: "Mes rapports", icon: FileStack, roles: ["field_epi", ...PILLAR] },
  { to: "/import-word", label: "Importer un rapport Word", icon: UploadCloud, roles: ["field_epi"] },
  { to: "/generated", label: "Rapports à revoir", icon: FileStack, roles: OV },
  { to: "/library", label: "Bibliothèque", icon: Library, roles: SURV },
  { to: "/indicators", label: "Indicateurs", icon: Gauge, roles: OV },
  { to: "/terrain", label: "Terrain", icon: ListTodo, roles: SURV },
  { to: "/actions", label: "Suivi des actions", icon: ClipboardCheck, roles: SURV },
  { to: "/qualite", label: "Qualité des données", icon: ShieldCheck, roles: OV },
  { to: "/map", label: "Carte", icon: Map, roles: OV },
  { to: "/download", label: "Installer Stride", icon: DownloadCloud },
  { to: "/profile", label: "Mon Profil", icon: UserCircle },
  { to: "/admin", label: "Gestion des utilisateurs", icon: Settings, roles: ["admin"] },
];

const ADVANCED = [
  { section: "Rapportage harmonisé" },
  { to: "/dashboard", label: "Tableau de bord détaillé", icon: LayoutDashboard, roles: OV },
  { to: "/consolidated", label: "Rapport Consolidé", icon: Layers3, roles: OV },
  { to: "/register", label: "Registre de Saisie", icon: ClipboardCheck, roles: OV },
  { to: "/monthly", label: "Rapport Mensuel", icon: FileText },
  { section: "Surveillance (anciens modules)" },
  { to: "/new-report", label: "Nouveau Rapport", icon: FilePlus2, roles: ["field_epi", "provincial_supervisor", "admin"] },
  { to: "/reports", label: "Rapports Quotidiens", icon: ClipboardList, roles: SURV },
  { to: "/weekly", label: "Rapport Hebdomadaire", icon: CalendarRange, roles: OV },
  { to: "/dos", label: "Synthèse DOS", icon: Presentation, roles: OV },
  { to: "/data-quality", label: "Qualité des Données (ancien)", icon: ShieldCheck, roles: OV },
  { to: "/signals", label: "Signaux & Alertes", icon: Siren, roles: SURV },
  { to: "/alerts-history", label: "Historique des Alertes", icon: MailCheck, roles: ["si_analyst", "program_manager", "admin"] },
  { to: "/recommendations", label: "Recommandations", icon: ListTodo, roles: SURV },
  { to: "/health-zones", label: "Zones de Santé", icon: MapPinned, roles: OV },
  { to: "/provinces", label: "Provinces", icon: Building2, roles: OV },
  { section: "Pilier Laboratoire", roles: ["lab_tech", ...OV] },
  { to: "/lab", label: "Labo · Tableau", icon: FlaskConical, roles: ["lab_tech", ...OV] },
  { to: "/lab/new", label: "Labo · Saisie", icon: FlaskConical, roles: ["lab_tech", ...OV] },
  { section: "Autres Piliers", roles: ["rcce_agent", "eds_agent", "ipc_agent", "case_mgmt_agent", ...OV] },
  { to: "/multi-pillar", label: "Multi-Piliers", icon: LayoutGrid, roles: OV },
  { to: "/pillar/rcce", label: "CREC / RCCE", icon: Layers, roles: ["rcce_agent", ...OV] },
  { to: "/pillar/eds", label: "EDS / SDB", icon: Layers, roles: ["eds_agent", ...OV] },
  { to: "/pillar/ipc", label: "PCI", icon: Layers, roles: ["ipc_agent", ...OV] },
  { to: "/pillar/case_mgmt", label: "Prise en Charge", icon: Layers, roles: ["case_mgmt_agent", ...OV] },
  { section: "Système" },
  { to: "/data-import", label: "Import de Données", icon: UploadCloud, roles: ["si_analyst", "admin", "provincial_supervisor"] },
  { to: "/kobo", label: "KoboToolbox", icon: UploadCloud, roles: ["si_analyst", "admin"] },
];

const ADV_KEY = "stride_advanced_nav";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const { data: cfg } = useConfig();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [advanced, setAdvanced] = useState(localStorage.getItem(ADV_KEY) === "1");
  const toggleAdvanced = () => { localStorage.setItem(ADV_KEY, advanced ? "0" : "1"); setAdvanced(!advanced); };
  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("");
  const chooseLang = (l) => { setLang(l); if (user) api.post("/auth/language", { language: l }).catch(() => {}); };

  const role = user?.role;
  const can = (n) => !n.roles || n.roles.includes(role);
  const withSections = (list) => {
    const vis = list.filter(can);
    return vis.filter((n, i) => {
      if (!n.section) return true;
      for (let j = i + 1; j < vis.length; j++) { if (vis[j].section) break; return true; }
      return false;
    });
  };
  const items = NAV.filter(can);
  const advItems = ADV.includes(role) ? withSections(ADVANCED) : [];

  const renderItem = (n, idx) => (
    n.section ? (
      <p key={`sec-${idx}`} className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{t(n.section)}</p>
    ) : (
      <NavLink
        key={`${n.to}-${idx}`}
        to={n.to}
        onClick={() => setOpen(false)}
        data-testid={`nav-${n.to.replace(/\//g, "")}`}
        className={({ isActive }) =>
          `flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] font-medium transition-colors ${
            isActive ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-slate-100"
          }`
        }
      >
        <n.icon className="h-4 w-4 shrink-0" />
        {t(n.label)}
      </NavLink>
    )
  );

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="h-14 flex items-center gap-2.5 px-5 border-b border-white/10">
        <Logo size={24} tone="light" />
        <div className="leading-tight">
          <p className="font-semibold text-white text-sm tracking-tight">Stride</p>
          <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Surveillance</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
        {items.map(renderItem)}
        {advItems.length > 0 && (
          <>
            <button type="button" onClick={toggleAdvanced} data-testid="nav-advanced-toggle"
              className="mt-4 w-full flex items-center justify-between px-3 py-2 rounded-md text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:bg-white/5 hover:text-slate-300">
              <span className="flex items-center gap-2"><Wrench className="h-3.5 w-3.5" />{t("Mode avancé")}</span>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${advanced ? "rotate-180" : ""}`} />
            </button>
            {advanced && <div className="space-y-0.5 pl-1 border-l border-white/10 ml-3" data-testid="nav-advanced">{advItems.map(renderItem)}</div>}
          </>
        )}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="flex items-center gap-3 px-2 py-2">
          {user?.photo_path
            ? <img src={`${BACKEND}/api/mr-files/${user.photo_path}?auth=${localStorage.getItem("token")}`} alt="" className="h-9 w-9 rounded-full object-cover bg-slate-200" />
            : <div className="h-8 w-8 rounded-md bg-white/10 text-white flex items-center justify-center text-xs font-semibold">{initials}</div>}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-[11px] text-slate-500 truncate">{t(ROLE_LABELS[user?.role])}</p>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={() => { logout(); navigate("/login"); }}
          className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-400 hover:bg-white/5 hover:text-white transition-colors"
        >
          <LogOut className="h-4 w-4" /> {t("Se déconnecter")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-60 bg-[#0F172A] border-r border-slate-900 flex-col z-30">
        <SidebarContent />
      </aside>

      {/* mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="relative w-60 bg-[#0F172A] flex flex-col">
            <button className="absolute top-4 right-3 text-slate-400" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="lg:pl-60">
        <header className="h-14 bg-white/95 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-20 flex items-center justify-between px-4 sm:px-6">
          <button className="lg:hidden text-slate-600" onClick={() => setOpen(true)} data-testid="menu-btn"><Menu className="h-6 w-6" /></button>
          <div className="hidden lg:block text-sm text-slate-500">{t("République Démocratique du Congo · Pilier Surveillance")}</div>
          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-md border border-slate-200 overflow-hidden text-xs font-semibold" data-testid="lang-switcher" title="Langue / Language">
              <Languages className="h-3.5 w-3.5 text-slate-400 ml-2 mr-1" />
              <button onClick={() => chooseLang("fr")} data-testid="lang-fr"
                className={`px-2.5 py-1 transition-colors ${lang === "fr" ? "bg-[#F05A28] text-white" : "text-slate-500 hover:bg-slate-100"}`}>FR</button>
              <button onClick={() => chooseLang("en")} data-testid="lang-en"
                className={`px-2.5 py-1 transition-colors ${lang === "en" ? "bg-[#F05A28] text-white" : "text-slate-500 hover:bg-slate-100"}`}>EN</button>
            </div>
            {cfg?.demo_mode !== false && <span className="hidden sm:inline demo-badge text-[11px] font-semibold text-amber-900 px-3 py-1 rounded border border-amber-300">{t("Données de démonstration")}</span>}
          </div>
        </header>
        <OfflineBar />
        <main className="p-4 sm:p-6 max-w-[1600px] mx-auto">{children}</main>
      </div>
      <InstallPrompt />
    </div>
  );
}
